import React, { useState, useEffect, useContext } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { all_routes } from "../router/all_routes";
import { API_BASE_URL } from "../../environment";
import { AuthContext } from "../../context/authcontext";

const MySwal = withReactContent(Swal);

const AccountVerification = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const route = all_routes;
  const { auth } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  useEffect(() => {
    if (auth) {
      navigate(route.dealsDashboard, { replace: true });
      return;
    }
  }, [auth, navigate, route.dealsDashboard]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleVerify();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleVerify = async () => {
    if (!token) {
      await MySwal.fire({
        icon: "error",
        title: "Verification Error",
        text: "Verification token is missing.",
        confirmButtonText: "OK",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/signup-verification/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();

      if (!res.ok) {
        if (data.error?.includes("Invalid or expired")) {
          const result = await MySwal.fire({
            icon: "error",
            title: "Link Expired",
            html: `
      <p>Your verification link is invalid or has expired.</p>
      <p>Click below to sign up again.</p>
    `,
            showConfirmButton: true,
            confirmButtonText: "Go to Sign Up",
            width: 450,
            customClass: {
              popup: "rounded-2xl p-4",
              title: "fs-24 fw-bold mb-2",
              htmlContainer: "fs-16 text-gray-700",
              confirmButton: "btn btn-primary w-100 mt-3",
            },
          });

          if (result.isConfirmed) {
            navigate(route.register);
          }
          return;
        }

        await MySwal.fire({
          icon: "error",
          title: "Verification Error",
          text: data.message || "Something went wrong. Please try again.",
          confirmButtonText: "OK",
        });
      } else {
        navigate(route.login);
      }
    } catch (err: any) {
      await MySwal.fire({
        icon: "error",
        title: "Network Error",
        text:
          err.message || "Unable to verify right now. Please try again later.",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-content">
      <div className="d-flex flex-wrap w-100 vh-100 overflow-hidden account-bg-04">
        <div className="d-flex align-items-center justify-content-center flex-wrap vh-100 overflow-auto p-4 w-50 bg-backdrop">
          <div className="mx-auto mw-450 text-center">
            <div className="mb-4">
              <ImageWithBasePath
                src="assets/img/Logo.png"
                className="img-fluid"
                alt="Logo"
              />
            </div>

            <h4 className="mb-3 fs-20">Verify Your Account</h4>
            <p className="mb-4">
              {loading ? "Verifying your email address..." : "Please wait while we verify your email address."}
            </p>

            <div className="text-center mt-4">
              <p className="fw-medium text-gray">Copyright © 2024 - CRMS</p>
              <Link
                to={route.login}
                className="d-block mt-2 text-purple link-hover"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
        <div className="w-50 vh-100 d-none d-md-block bg-gray-100" />
      </div>
    </div>
  );
};

export default AccountVerification;
